<script setup lang="ts">
import type {
  FormConfig,
  FormField,
  FormFieldChanges,
  StudyChanges
} from '@/models';
import { FORM_IMPORT_FIELD_HEADERS } from '@/utils';
import type { PropType } from 'vue';

const props = defineProps({
  formConfig: {
    type: Object as PropType<FormConfig>,
    required: true
  }
});

const getChange = (key: string) => {
  return props.formConfig.formChanges.changes[key as keyof StudyChanges] ||
    props.formConfig.formChanges.changes[key as keyof StudyChanges] === '' ?
    props.formConfig.formChanges.changes[key as keyof StudyChanges] :
    props.formConfig.formChanges.previous[key as keyof StudyChanges];
};

const getCssClass = (key: string, previous?: boolean): string =>
  props.formConfig.formChanges.changes[key as keyof StudyChanges] === undefined ?
    'white' :
    props.formConfig.formChanges.previous[key as keyof StudyChanges] !==
      props.formConfig.formChanges.changes[key as keyof StudyChanges] ?
      previous ?
        'old' :
        'new' :
      'white';

const getItemCssClass = (item: FormField) => {
  return item.changes && item.changes.deleted ?
    'deleted' :
    item.changes && item.changes.newField ?
      'added' :
      'white';
};

const checkDiff = (
  previous: string | number | undefined,
  changed?: string | number | undefined,
  old?: boolean
) => {
  if (changed == undefined || changed == `undefined`) {
    return 'white';
  }
  const diff = previous != changed;
  return diff ? (old ? 'old' : 'new') : 'white';
};

const getPreviousItemOrderClassName = (item: FormField) => {
  return item.changes && item.changes.deleted ?
    'old' :
    item.changes && item.changes.newField ?
      'new' :
      checkDiff(
        item.changes && item.changes.moved ?
          item.changes.moved.previous :
          'NA',
        item.changes && item.changes.moved ?
          item.changes.moved.current :
          'NA',
        true
      );
};

const getPreviousItemClassName = (item: FormField, key: string) => {
  return item.changes && item.changes.deleted ?
    'old' :
    item.changes && item.changes.newField ?
      'new' :
      checkDiff(
        `${JSON.stringify(item[key as keyof FormField])}`,
        item.changes ?
          `${JSON.stringify(item.changes[key as keyof FormFieldChanges])}` :
          undefined,
        true
      );
};

const getPreviousItemOrder = (item: FormField, index: number) => {
  return item.changes && item.changes.newField ?
    item.changes.moved?.current :
    item.changes && item.changes.moved ?
      item.changes.moved.previous :
      index + 1;
};

const getChangedItemOrderClassName = (item: FormField, index: number) => {
  return checkDiff(
    item.changes && item.changes.moved ?
      item.changes.moved.previous :
      index + 1,
    item.changes && item.changes.moved ? item.changes.moved.current : index + 1
  );
};

const getChangedItemValues = (item: FormField, key: string) => {
  return item.changes && item.changes![key as keyof FormFieldChanges] || `${item.changes![key as keyof FormFieldChanges]}` === '' ?
    typeof item.changes![key as keyof FormFieldChanges] === 'string' ||
      typeof item.changes![key as keyof FormFieldChanges] === 'number' ?
      item.changes![key as keyof FormFieldChanges] :
      JSON.stringify(item.changes![key as keyof FormFieldChanges]) :
    typeof item[key as keyof FormField] === 'string' ||
      item[key as keyof FormField] === 'number' ?
      item[key as keyof FormField] :
      JSON.stringify(item[key as keyof FormField]);
};
</script>

<template>
  <div class="tab-root">
    <table className="form-table">
      <caption>
        Form config
      </caption>
      <thead>
        <tr>
          <th>identifier</th>
          <th>Title</th>
          <th>Description</th>
          <th>Enforcements</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="Object.keys(formConfig.formChanges.previous).length > 0">
          <td :class="getCssClass('identifier', true)">
            {{ formConfig.formChanges.previous.identifier }}
          </td>
          <td :class="getCssClass('title', true)">
            {{ formConfig.formChanges.previous.title }}
          </td>
          <td :class="getCssClass('description', true)">
            {{ formConfig.formChanges.previous.description }}
          </td>
          <td :class="getCssClass('description', true)">
            {{ formConfig.formChanges.previous.enforcements }}
          </td>
        </tr>
        <tr v-if="Object.keys(formConfig.formChanges.changes).length > 0">
          <td :class="getCssClass('identifier')">
            {{ getChange("identifier") }}
          </td>
          <td :class="getCssClass('title')">
            {{ getChange("title") }}
          </td>
          <td :class="getCssClass('description')">
            {{ getChange("description") }}
          </td>
          <td :class="getCssClass('enforcements')">
            {{ getChange("enforcements") }}
          </td>
        </tr>
      </tbody>
    </table>

    <br />
    <br />

    <div className="field-updates">
      <div>
        Fields Updated: {{ formConfig.formChanges.fieldUpdates.fieldsChanged }}
      </div>
      <div className="fields-added">
        Fields Added: {{ formConfig.formChanges.fieldUpdates.fieldsAdded }}
      </div>
      <div className="fields-deleted">
        Fields Deleted: {{ formConfig.formChanges.fieldUpdates.fieldsDeleted }}
      </div>
    </div>

    <br />

    <table className="field-table">
      <caption>
        Field config
      </caption>
      <thead>
        <tr>
          <th></th>
          <th>Order</th>
          <th v-for="header in FORM_IMPORT_FIELD_HEADERS" :key="header">
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in formConfig.items"
          :key="item.identifier">
          <tr :class="getItemCssClass(item)">
            <td>
              {{
                item.changes && item.changes.newField
                  ? "+"
                  : item.changes
                  ? "-"
                  : ""
              }}
            </td>
            <td :class="getPreviousItemOrderClassName(item)">
              {{ getPreviousItemOrder(item, index) }}
            </td>
            <td v-for="header in FORM_IMPORT_FIELD_HEADERS"
              :key="header"
              :class="getPreviousItemClassName(item, header)">
              {{
                typeof item[header as keyof FormField] === "string" ||
                item[header as keyof FormField] === "number"
                  ? item[header as keyof FormField]
                  : JSON.stringify(item[header as keyof FormField])
              }}
            </td>
          </tr>

          <tr v-if="
              item.changes && !item.changes.deleted && !item.changes.newField
            ">
            <td>+</td>
            <td :class="getChangedItemOrderClassName(item, index)">
              {{
                item.changes && item.changes.moved
                  ? item.changes.moved.current
                  : index
              }}
            </td>
            <td v-for="header in FORM_IMPORT_FIELD_HEADERS"
              :key="header"
              :class="checkDiff(
                `${JSON.stringify(item[header as keyof FormField])}`,
                item.changes ? `${JSON.stringify(item.changes[header as keyof FormFieldChanges])}` : undefined
              )">
              {{getChangedItemValues(item, header)}}
              <FontAwesomeIcon :icon="[`fas`, `circle-exclamation`]"
                class="exclamation-icon"
                v-tooltip="item.errors[header as keyof StudyChanges]"
                v-if="item.errors &&
                    item.changes &&
                    item.errors[header as keyof StudyChanges]"/>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
$old: #ffe8e9;
$new: #dcffdc;
.tab-root {
  width: 100%;
  height: 100%;
  overflow: scroll;
}

.form-table,
.field-table {
  font-family: arial, sans-serif;
  border-collapse: collapse;

  td,
  th {
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
  }

  .cell {
    display: flex;
    justify-content: space-between;

    .error {
      color: red;
    }
  }
}

.old {
  background-color: $old;
}

.new {
  background-color: $new;
}

.deleted {
  background-color: #cccccc;
  color: #666666;

  .old {
    background-color: #cccccc;
  }
}

.added {
  background-color: $new;
}

.field-updates {
  display: flex;
  font-weight: 700;
}

.fields-added,
.fields-deleted {
  margin-left: 32px;
}

.exclamation-icon {
    color: red;
}
</style>
